import {errors} from '../utils/errorEvents';
import {pricingHelper} from '../utils/pricingHelper';
import {writeCookie, TRIP_COOKIE} from '../utils/cookieHelpers';
import {
    getMaxAdultsFromHouseRules,
    hasAdultSupervision,
    hasValidPets,
    hasValidGuestCount,
    hasTooManyGuests
} from '../utils/guests';
import {hasValidDates} from '../utils/dateValidationHelper';
import tripActions from '../actions/tripActions';
import listingActions from '../actions/listingActions';
import listingExtendedActions from '../actions/listingExtendedActions';
import EdapActions from '../actions/edapActions';
import getUnitId from '../utils/getUnitId';
import redirectToPinnedSERP from '../utils/redirectToPinnedSERP';
import {isInquiryModalOpen} from '../selectors/modalStates';
import {getArrivedFromExternalSiteWithoutDates} from '../selectors/system';
import {Map} from 'immutable';
import differenceInDays from 'date-fns/difference_in_days';

function setBookingFlow(store, action = {payload: {}}) {
    const {
        isBookingFlow
    } = action.payload;

    store.dispatch(tripActions.tripDetailsDataUpdated({isBookingFlow}));
}

function isNotUndefined(value) {
    return typeof value !== 'undefined';
}

function getDateDataFromUpdate(targetValues, fieldsChanged, store) {
    const state = store.getState();
    const {currentDate} = state.tripDetailsReducer;
    const {availabilityCalendar} = state.listingReducer;

    const result = {
        newArriveDate: targetValues.newArriveDate,
        newDepartDate: targetValues.newDepartDate,
        datesUpdated: false
    };

    if (isNotUndefined(fieldsChanged.arriveDateText) || isNotUndefined(fieldsChanged.departDateText)) {
        result.datesUpdated = true;
        result.newArriveDate = fieldsChanged.arriveDateText;
        result.newDepartDate = fieldsChanged.departDateText;
    }

    const skipAvailabilityValidation = getArrivedFromExternalSiteWithoutDates(state);

    const dateValidity = hasValidDates({
        currentDate,
        arriveDateText: result.newArriveDate,
        departDateText: result.newDepartDate,
        availabilityCalendar,
        skipAvailabilityValidation
    });

    result.dateValidity = dateValidity;

    store.dispatch(listingActions.datesAreValid(dateValidity));

    return result;
}

/* eslint-disable complexity */
function getGuestDataFromUpdate({targetValues, fieldsChanged, sleeps, maxAdults, isInitialPageLoad}) {
    let result = {
        guestsUpdated: isNotUndefined(fieldsChanged.adults) || isNotUndefined(fieldsChanged.children) || isNotUndefined(fieldsChanged.hasPets)
    };

    if (isNotUndefined(fieldsChanged.adults)) {
        result.newAdults = fieldsChanged.adults;
    } else {
        result.newAdults = targetValues.newAdults || 0;
    }

    if (result.newAdults === 0 && isInitialPageLoad) {
        result.newAdults = Math.min(sleeps, 2);
        result.usedDefaultAdults = true;
    }

    if (isNotUndefined(fieldsChanged.children)) {
        result.newChildren = fieldsChanged.children;
    } else {
        result.newChildren = targetValues.newChildren || 0;
    }

    if (isNotUndefined(fieldsChanged.hasPets)) {
        result.newPets = fieldsChanged.hasPets;
    } else {
        result.newPets = targetValues.newPets || false;
    }

    result.validGuestCount = hasValidGuestCount({
        adults: result.newAdults,
        children: result.newChildren,
        maxAdults,
        sleeps,
        // On initial page load, we will allow 0 guest selection, and later interpret it as two adults.
        isEmptyValid: isInitialPageLoad
    });

    return result;
}
/* eslint-enable complexity */

function shouldUpdatePrice(targetValues, tripDetails) { // eslint-disable-line complexity
    // should update price with any change in trip details
    const dateValueChanged =
        (targetValues.newArriveDate !== tripDetails.arriveDateText) ||
        (targetValues.newDepartDate !== tripDetails.departDateText);
    const guestValueChanged =
        (targetValues.newAdults !== tripDetails.adults) ||
        (targetValues.newChildren !== tripDetails.children) ||
        (targetValues.newPets !== tripDetails.hasPets);

    return (dateValueChanged && targetValues.datesUpdated) || (guestValueChanged && targetValues.guestsUpdated);
}

function shouldGetNewPrice(targetValues) {
    return targetValues.dateValidity.datesValid && targetValues.validGuestCount;
}

function getDefaultValues(tripData) {
    return {
        datesUpdated: false,
        guestsUpdated: false,
        newAdults: tripData.adults,
        newArriveDate: tripData.arriveDateText,
        newChildren: tripData.children,
        newDepartDate: tripData.departDateText,
        newPets: tripData.hasPets
    };
}

function getTargetValues(store, fieldsChanged, isInitialPageLoad) {
    const state = store.getState();
    const {houseRules, sleeps} = state.listingReducer;
    const tripData = state.tripDetailsReducer;
    const defaultValues = getDefaultValues(tripData);
    const targetValues = Object.assign({}, defaultValues);
    const maxAdults = getMaxAdultsFromHouseRules(houseRules);

    const dateData = getDateDataFromUpdate(targetValues, fieldsChanged, store);
    const guestData = getGuestDataFromUpdate({targetValues, fieldsChanged, sleeps, maxAdults, isInitialPageLoad});

    return {
        ...targetValues,
        ...guestData,
        ...dateData
    };
}

/* eslint-disable complexity */
function tripDetailsEdapCheck(store, action = {payload: {}}) {
    const state = store.getState();
    const listing = state.listingReducer;
    const tripDetailsFromState = state.tripDetailsReducer;
    const dateValidityFromState = state.listingPageReducer.validDates;
    const {tripDetails: tripDetailsFromPayload} = action.payload;
    const {dateValidity: dateValidityFromPayload} = action.payload;
    const {sleeps, petsAllowed} = listing;

    let adults;
    let children;
    let hasPets;
    let arriveDateText;
    let departDateText;
    let hasMinStayError;
    let hasSameDayArrivalError;
    let hasUnavailableDates;

    if (tripDetailsFromPayload) {
        ({adults, children, hasPets, arriveDateText, departDateText} = tripDetailsFromPayload);
    } else {
        ({adults, children, hasPets, arriveDateText, departDateText} = tripDetailsFromState);
    }

    if (dateValidityFromPayload) {
        ({hasMinStayError, hasSameDayArrivalError, hasUnavailableDates} = dateValidityFromPayload);
    } else {
        ({hasMinStayError, hasSameDayArrivalError, hasUnavailableDates} = dateValidityFromState);
    }

    const guestsChanged = isNotUndefined(adults) && isNotUndefined(children);
    const datesChanged = isNotUndefined(arriveDateText) && isNotUndefined(departDateText);

    //  If there are multiple errors, we want to throw only one edap event, which
    //  should align with the UI.  Follow this rule:
    //  Dates -> Guests -> Pets
    //  Should be similar/same to what is in the trip selector:: getCurrentError
    if (datesChanged && hasMinStayError) {
        store.dispatch(EdapActions.triggerEdapEvent('generic.event', {
            eventaction: 'dates_error',
            eventcategory: 'pdp_error',
            eventlabel: 'minimum-stay-not-met'
        }));
    } else if (datesChanged && hasSameDayArrivalError) {
        store.dispatch(EdapActions.triggerEdapEvent('generic.event', {
            eventaction: 'dates_error',
            eventcategory: 'pdp_error',
            eventlabel: 'same-day-arrival'
        }));
    } else if (datesChanged && hasUnavailableDates) {
        store.dispatch(EdapActions.triggerEdapEvent('generic.event', {
            eventcategory: errors.UNAVAILABLE_DATES.edapCategory,
            eventaction: errors.UNAVAILABLE_DATES.edapAction,
            eventlabel: errors.UNAVAILABLE_DATES.edapLabel
        }));
    } else if (guestsChanged && hasTooManyGuests({adults, children, sleeps})) {
        store.dispatch(EdapActions.triggerEdapEvent('generic.event', {
            eventcategory: errors.OVER_MAX_OCCUPANCY.edapCategory,
            eventaction: errors.OVER_MAX_OCCUPANCY.edapAction,
            eventlabel: errors.OVER_MAX_OCCUPANCY.edapLabel
        }));
    } else if (guestsChanged && !hasAdultSupervision({adults, children})) {
        store.dispatch(EdapActions.triggerEdapEvent('generic.event', {
            eventaction: 'guests_error',
            eventcategory: 'pdp_error',
            eventlabel: 'adults needed'
        }));
    } else if (isNotUndefined(hasPets) && !hasValidPets({hasPets, petsAllowed})) {
        store.dispatch(EdapActions.triggerEdapEvent('generic.event', {
            eventaction: 'guests_error',
            eventcategory: 'pdp_error',
            eventlabel: 'pets-not-allowed'
        }));
    }
}

function updateAdl(targetValues) {
    if (isNotUndefined(targetValues.newAdults)) {
        window.analyticsdatalayer.numadults = (targetValues.newAdults || 0).toString();
    }

    if (isNotUndefined(targetValues.newChildren)) {
        window.analyticsdatalayer.numchildren = (targetValues.newChildren || 0).toString();
    }

    if (isNotUndefined(targetValues.newPets)) {
        window.analyticsdatalayer.withpets = (targetValues.newPets || false).toString();
    }

    if (isNotUndefined(targetValues.newArriveDate)) {
        window.analyticsdatalayer.startdate = (targetValues.newArriveDate || '').toString();
    }

    if (isNotUndefined(targetValues.newDepartDate)) {
        window.analyticsdatalayer.enddate = (targetValues.newDepartDate || '').toString();
    }

    if (targetValues.datesUpdated) {
        const arriveDate = targetValues.newArriveDate || window.analyticsdatalayer.startdate;
        const departDate = targetValues.newDepartDate || window.analyticsdatalayer.enddate;
        const lengthOfStay = differenceInDays(departDate, arriveDate) || -1;

        window.analyticsdatalayer.lengthofstay = lengthOfStay.toString();
    }
}
/* eslint-enable complexity */

function tripDetailsChanged(store, action = {payload: {}}) {
    const state = store.getState();
    const listing = state.listingReducer;
    const system = state.systemReducer;
    const {fieldsChanged, isInitialPageLoad} = action.payload;
    const {
        loadingRate,
        loadedRate
    } = listingActions;

    // fieldsChanged gets mutated in getTargetValues, so copy the original for edap tests.
    const originalFieldsChanged = Object.assign({}, fieldsChanged);
    const targetValues = getTargetValues(store, fieldsChanged, isInitialPageLoad);

    updateAdl(targetValues);

    store.dispatch(tripActions.tripDetailsEdapCheck(originalFieldsChanged, targetValues.dateValidity));

    // Persist the data we know so far if client-side
    writeCookie(TRIP_COOKIE, {
        arrival: targetValues.newArriveDate,
        departure: targetValues.newDepartDate,
        numberOfAdults: targetValues.usedDefaultAdults ? 0 : targetValues.newAdults,
        numberOfChildren: targetValues.newChildren,
        petIncluded: targetValues.newPets
    });

    const arrivedFromExternalSiteWithoutDates = getArrivedFromExternalSiteWithoutDates(state);
    const inquiryModalOpen = isInquiryModalOpen(state);
    const datesValidOutsideInquiryModal = targetValues.dateValidity.datesValid && !inquiryModalOpen
    const shouldRedirect = arrivedFromExternalSiteWithoutDates && datesValidOutsideInquiryModal;

    if (shouldRedirect) {
        store.dispatch(tripActions.tripDetailsDataUpdated(fieldsChanged));
        redirectToPinnedSERP(listing, system, targetValues);
        
        return Promise.resolve();
    }

    if (inquiryModalOpen) {
        store.dispatch(tripActions.tripDetailsDataUpdated({arrivedWithoutDates: false}));
    }

    if (shouldUpdatePrice(targetValues, state.tripDetailsReducer) || isInitialPageLoad) {
        store.dispatch(listingActions.datesAreValid(targetValues.dateValidity));
        store.dispatch(listingActions.guestsAreValid(true));
        store.dispatch(tripActions.tripDetailsDataUpdated(fieldsChanged));
        store.dispatch(listingExtendedActions.fetchUrgencyMessage(targetValues.newArriveDate, targetValues.newDepartDate));

        if (shouldGetNewPrice(targetValues)) {
            return pricingHelper({
                store,
                listingId: listing.listingId,
                site: system.siteName,
                displayLocale: system.locale,
                arrival: targetValues.newArriveDate,
                departure: targetValues.newDepartDate,
                adults: targetValues.newAdults,
                children: targetValues.newChildren,
                pets: targetValues.newPets,
                currency: system.currency,
                loadingRate,
                loadedRate,
                isOnlineBookable: listing.onlineBookable,
                acceptsHomeAwayPayments: listing.acceptsHomeAwayPayments,
                unitId: getUnitId(listing.unitApiUrl),
                pageLoadPriceCall: isInitialPageLoad,
                useMockData: system.useMockData,
                unitApiUrl: listing.unitApiUrl
            });
        }

        // otherwise reset
        const pricingKey = new Map({
            arrival: targetValues.newArriveDate,
            departure: targetValues.newDepartDate,
            adults: targetValues.newAdults,
            children: targetValues.newChildren,
            pets: targetValues.newPets,
            currency: system.currency
        });
        store.dispatch(listingActions.pricingInfoReset(pricingKey));
        store.dispatch(listingActions.loadingRate(false));
    }

    return Promise.resolve();
}

export default tripDetailsChanged;
export {
    getTargetValues,
    getDefaultValues,
    getDateDataFromUpdate,
    getGuestDataFromUpdate,
    isNotUndefined,
    setBookingFlow,
    shouldUpdatePrice,
    shouldGetNewPrice,
    tripDetailsChanged,
    tripDetailsEdapCheck
};
